/**
 * RubricBuilder — Coach-facing rubric editor.
 *
 * Features:
 *   - Add/remove criteria rows
 *   - Each criterion: name, description, weight
 *   - Each criterion has scoring levels (e.g., Excellent/4, Good/3, …)
 *   - Move criteria up/down to reorder
 *   - Preview mode showing rubric as a table
 *   - Save button posting to POST /scenario-os/coach/rubrics/
 */
import React, { useState, useCallback } from 'react';

const DEFAULT_LEVELS = [
  { label: 'Excellent', score: 4, description: '' },
  { label: 'Good', score: 3, description: '' },
  { label: 'Satisfactory', score: 2, description: '' },
  { label: 'Needs Work', score: 1, description: '' },
];

function newCriterion() {
  return {
    id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    description: '',
    weight: 1.0,
    levels: DEFAULT_LEVELS.map((l) => ({ ...l })),
  };
}

export default function RubricBuilder({ sceneId, domain, existingRubric, onSaved }) {
  const [title, setTitle] = useState(existingRubric?.title || '');
  const [criteria, setCriteria] = useState(() => {
    if (existingRubric?.criteria?.length) {
      return existingRubric.criteria.map((c, i) => ({ ...c, id: c.id || `c_${i}` }));
    }
    return [newCriterion()];
  });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const addCriterion = () => setCriteria((prev) => [...prev, newCriterion()]);

  const removeCriterion = (idx) => {
    setCriteria((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveCriterion = (idx, dir) => {
    setCriteria((prev) => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const updateCriterion = (idx, field, value) => {
    setCriteria((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const updateLevel = (cIdx, lIdx, field, value) => {
    setCriteria((prev) =>
      prev.map((c, i) => {
        if (i !== cIdx) return c;
        const levels = c.levels.map((l, j) => (j === lIdx ? { ...l, [field]: value } : l));
        return { ...c, levels };
      })
    );
  };

  const addLevel = (cIdx) => {
    setCriteria((prev) =>
      prev.map((c, i) => {
        if (i !== cIdx) return c;
        return { ...c, levels: [...c.levels, { label: '', score: 0, description: '' }] };
      })
    );
  };

  const removeLevel = (cIdx, lIdx) => {
    setCriteria((prev) =>
      prev.map((c, i) => {
        if (i !== cIdx) return c;
        return { ...c, levels: c.levels.filter((_, j) => j !== lIdx) };
      })
    );
  };

  const maxScore = criteria.reduce((sum, c) => {
    const topLevel = Math.max(...c.levels.map((l) => l.score || 0), 0);
    return sum + topLevel * (c.weight || 1);
  }, 0);

  const handleSave = useCallback(async () => {
    setError('');
    setSuccess('');
    if (!title.trim()) { setError('Rubric title is required.'); return; }
    if (criteria.some((c) => !c.name.trim())) { setError('All criteria need a name.'); return; }

    setSaving(true);
    const token = localStorage.getItem('scenario_os_token');
    try {
      const res = await fetch('/scenario-os/coach/rubrics/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          scene_id: sceneId,
          domain: domain || '',
          title: title.trim(),
          criteria: criteria.map(({ id, ...rest }) => rest),
          max_score: maxScore || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSuccess('Rubric saved successfully.');
      if (onSaved) onSaved(data);
    } catch (e) {
      setError(e.message || 'Failed to save rubric.');
    } finally {
      setSaving(false);
    }
  }, [title, criteria, sceneId, domain, maxScore, onSaved]);

  if (preview) {
    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#1f2937' }}>{title || 'Untitled Rubric'}</h2>
          <button onClick={() => setPreview(false)} style={secondaryBtnStyle}>← Edit</button>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Max score: <strong>{maxScore}</strong>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Criterion</th>
                <th style={{ ...thStyle, width: 60 }}>Weight</th>
                {(criteria[0]?.levels || []).map((l, i) => (
                  <th key={i} style={thStyle}>{l.label || `Level ${i + 1}`} ({l.score})</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criteria.map((c, i) => (
                <tr key={i}>
                  <td style={tdStyle}>
                    <strong>{c.name}</strong>
                    {c.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{c.description}</div>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.weight}</td>
                  {c.levels.map((l, j) => (
                    <td key={j} style={tdStyle}>
                      <div style={{ fontSize: 13, color: '#374151' }}>{l.description || '—'}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={primaryBtnStyle}>
            {saving ? 'Saving…' : 'Save Rubric'}
          </button>
        </div>
        {error && <div style={errorBannerStyle}>{error}</div>}
        {success && <div style={successBannerStyle}>{success}</div>}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: '#1f2937' }}>Rubric Builder</h2>
        <button onClick={() => setPreview(true)} style={secondaryBtnStyle}>Preview →</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Rubric Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Capstone Project Rubric"
          style={inputStyle}
        />
      </div>

      {criteria.map((c, cIdx) => (
        <div key={c.id} style={criterionCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
              Criterion {cIdx + 1}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => moveCriterion(cIdx, -1)} disabled={cIdx === 0} style={iconBtnStyle} title="Move up">↑</button>
              <button onClick={() => moveCriterion(cIdx, 1)} disabled={cIdx === criteria.length - 1} style={iconBtnStyle} title="Move down">↓</button>
              <button onClick={() => removeCriterion(cIdx)} style={{ ...iconBtnStyle, color: '#dc2626' }} title="Remove">✕</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={smallLabelStyle}>Name</label>
              <input
                value={c.name}
                onChange={(e) => updateCriterion(cIdx, 'name', e.target.value)}
                placeholder="e.g., Critical Thinking"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={smallLabelStyle}>Description</label>
              <input
                value={c.description}
                onChange={(e) => updateCriterion(cIdx, 'description', e.target.value)}
                placeholder="Optional details"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={smallLabelStyle}>Weight</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={c.weight}
                onChange={(e) => updateCriterion(cIdx, 'weight', parseFloat(e.target.value) || 1)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: 6 }}>
            <label style={smallLabelStyle}>Scoring Levels</label>
          </div>
          {c.levels.map((l, lIdx) => (
            <div key={lIdx} style={{ display: 'grid', gridTemplateColumns: '120px 60px 1fr 28px', gap: 6, marginBottom: 4, alignItems: 'center' }}>
              <input
                value={l.label}
                onChange={(e) => updateLevel(cIdx, lIdx, 'label', e.target.value)}
                placeholder="Label"
                style={smallInputStyle}
              />
              <input
                type="number"
                value={l.score}
                onChange={(e) => updateLevel(cIdx, lIdx, 'score', parseFloat(e.target.value) || 0)}
                style={smallInputStyle}
              />
              <input
                value={l.description}
                onChange={(e) => updateLevel(cIdx, lIdx, 'description', e.target.value)}
                placeholder="What this level looks like"
                style={smallInputStyle}
              />
              <button onClick={() => removeLevel(cIdx, lIdx)} style={{ ...iconBtnStyle, fontSize: 11, padding: 2 }} title="Remove level">✕</button>
            </div>
          ))}
          <button onClick={() => addLevel(cIdx)} style={addLevelBtnStyle}>+ Add Level</button>
        </div>
      ))}

      <button onClick={addCriterion} style={addCriterionBtnStyle}>+ Add Criterion</button>

      <div style={{ marginTop: 16, fontSize: 13, color: '#6b7280' }}>
        Max possible score: <strong>{maxScore}</strong>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button onClick={handleSave} disabled={saving} style={primaryBtnStyle}>
          {saving ? 'Saving…' : 'Save Rubric'}
        </button>
        <button onClick={() => setPreview(true)} style={secondaryBtnStyle}>
          Preview as Table
        </button>
      </div>

      {error && <div style={errorBannerStyle}>{error}</div>}
      {success && <div style={successBannerStyle}>{success}</div>}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  maxWidth: 860,
  margin: '0 auto',
  padding: 20,
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 };
const smallLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 2 };
const inputStyle = {
  width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
};
const smallInputStyle = { ...inputStyle, padding: '5px 7px', fontSize: 12 };
const criterionCardStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
  padding: 16, marginBottom: 12,
};
const iconBtnStyle = {
  background: 'none', border: '1px solid #d1d5db', borderRadius: 4,
  cursor: 'pointer', fontSize: 13, padding: '2px 6px', color: '#6b7280',
};
const addLevelBtnStyle = {
  background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer',
  fontSize: 12, fontWeight: 600, padding: '4px 0', marginTop: 4,
};
const addCriterionBtnStyle = {
  background: '#eff6ff', color: '#2563eb', border: '1px dashed #93c5fd',
  borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', width: '100%',
};
const primaryBtnStyle = {
  background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6,
  padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const secondaryBtnStyle = {
  background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6,
  padding: '7px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
};
const errorBannerStyle = {
  marginTop: 12, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca',
  borderRadius: 6, color: '#991b1b', fontSize: 13,
};
const successBannerStyle = {
  marginTop: 12, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0',
  borderRadius: 6, color: '#166534', fontSize: 13,
};
const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13,
};
const thStyle = {
  background: '#f1f5f9', padding: '8px 10px', textAlign: 'left',
  borderBottom: '2px solid #e2e8f0', fontSize: 12, fontWeight: 700, color: '#475569',
};
const tdStyle = {
  padding: '8px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top',
};
